// A MEETING WITH SOMEONE OUTSIDE THE SYSTEM, LINKED TO THE CLIENT OR LEAD THEY BECAME (077).
//
// Two ways in, one dialog:
//   mode 'lead'    "make them a lead": the lead form opens first, prefilled with the name
//                  (as the lead's name and its main contact) and the details; once it is
//                  saved, its meetings are linked to the new lead.
//   mode 'client'  "link to a client or lead": pick one that already exists.
//
// WHAT IS LINKED is the server's answer (meetings/link.js): this meeting and its follow-up
// chain, always — listed, not ticked, since one conversation is one person — plus any other
// meeting typed with the same name, each a checkbox that starts EMPTY. Names are typed
// freely, so two people with one name are never merged without someone choosing it.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { clientsApi } from '../../api';
import { meetingsApi } from '../../api/calendar';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import ClientPicker from '../../components/ClientPicker';
import AsyncBoundary, { ErrorText } from '../../components/Loader';
import DateText from '../../components/DateText';
import LeadModal from '../Leads/LeadModal';

const t = he.calendar;

const MeetingLine = ({ meeting }) => (
  <span className="link-meeting">
    <DateText value={meeting.meeting_date} /> · {meeting.start_time}
    {meeting.title && <> · {meeting.title}</>}
    {meeting.summary && <span className="muted"> · {meeting.summary.slice(0, 80)}</span>}
  </span>
);

function LinkStep({ meeting, mode, clientId: fixedClientId, onClose, onLinked }) {
  const { data, loading, error } = useAsync(() => Promise.all([
    meetingsApi.linkCandidates(meeting.id),
    mode === 'client' ? clientsApi.list('all') : Promise.resolve([]),
  ]), [meeting.id]);
  const [cand, clients] = data || [null, []];
  const [clientId, setClientId] = useState(fixedClientId || '');
  const [include, setInclude] = useState(() => new Set());
  const [addAsContact, setAddAsContact] = useState(true);
  const [saveError, setSaveError] = useState(null);

  const toggle = (id) => setInclude((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    try {
      await meetingsApi.link(meeting.id, {
        client_id: Number(clientId),
        include: [...include],
        add_as_contact: mode === 'client' && addAsContact,
      });
      onLinked();
    } catch (e) {
      setSaveError(e);
    }
  };

  return (
    <Modal title={t.linkTitle(meeting.external_name)} onClose={onClose} onSave={submit} saveLabel={t.linkConfirm}
      saveDisabled={!clientId || !cand}>
      <AsyncBoundary loading={loading} error={error}>
        {cand && (
          <>
            {mode === 'client' && (
              <ClientPicker label={t.linkToWhom} value={clientId} onChange={setClientId} clients={clients} />
            )}
            <h3 className="link-heading">{t.linkChain}</h3>
            <ul className="link-list">
              {cand.chain.map((m) => <li key={m.id}><MeetingLine meeting={m} /></li>)}
            </ul>
            {cand.same_name.length > 0 && (
              <>
                <h3 className="link-heading">{t.linkSameName(cand.name)}</h3>
                <ul className="link-list">
                  {cand.same_name.map((m) => (
                    <li key={m.id}>
                      <label className="check-row">
                        <input type="checkbox" checked={include.has(m.id)} onChange={() => toggle(m.id)} />
                        <MeetingLine meeting={m} />
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {mode === 'client' && (
              <label className="check-row">
                <input type="checkbox" checked={addAsContact} onChange={(e) => setAddAsContact(e.target.checked)} />
                {t.linkAddContact(cand.name)}
              </label>
            )}
          </>
        )}
      </AsyncBoundary>
      {saveError && <ErrorText error={saveError} />}
    </Modal>
  );
}

export default function LinkMeetingsDialog({ meeting, mode, onClose, onLinked }) {
  const [leadId, setLeadId] = useState(null);

  if (mode === 'lead' && !leadId) {
    return (
      <LeadModal
        lead={{
          name: meeting.external_name,
          owner_partner_id: meeting.owner_partner_id,
          notes: meeting.external_details || '',
          contacts: [{ name: meeting.external_name, role: meeting.external_details, is_main: true }],
        }}
        onClose={onClose}
        onSaved={(saved) => setLeadId(saved.id)}
      />
    );
  }
  return <LinkStep meeting={meeting} mode={mode} clientId={leadId} onClose={onClose} onLinked={onLinked} />;
}
